import { z } from "zod";

/** Contrats partagés front ↔ back. Un champ lu ici DOIT être émis là-bas. */

export const SessionSchema = z.object({
  accessToken: z.string(),
  nom: z.string(),
  role: z.enum(["admin", "staff", "client", "delegue"]),
  expire: z.string(),
  /**
   * Nul pour un collaborateur MDC — il voit tous les dossiers.
   * Renseigné pour un porteur de lien client, qui n'en voit qu'un.
   * Sans lui, le portail ne sait pas de quel dossier il parle.
   */
  dossierId: z.string().nullable(),
});
export type Session = z.infer<typeof SessionSchema>;

/**
 * Ce qu'un lien d'invitation permet de faire.
 *
 * ⛔ AUCUN JETON DE SESSION ICI, ET C'EST TOUT L'OBJET DE LA REFONTE.
 * Le lien ouvrait autrefois une session à lui seul : le détenir suffisait à
 * entrer. Il ne sert plus qu'une fois, à établir des identifiants.
 */
export const EtatInvitationSchema = z.object({
  /**
   * `ACreer` — lien valable, aucun accès encore établi.
   * `DejaCree` — lien valable, mais les accès existent : on mène à la connexion.
   */
  etat: z.enum(["ACreer", "DejaCree"]),
  nom: z.string().nullable(),
});

export const DocumentSchema = z.object({
  id: z.string(),
  cheminLogique: z.string(),
  nomFichier: z.string(),
  contentType: z.string(),
  taille: z.number(),
  sha256: z.string(),
  scelle: z.boolean(),
  altere: z.boolean(),
  /** Ce que la pièce EST — clé de nature. Nulle pour les dépôts antérieurs. */
  nature: z.string().nullable(),
  deposeLe: z.string(),
  deposePar: z.string().nullable(),
});
export type Document = z.infer<typeof DocumentSchema>;

/**
 * Le contenu documentaire : les pièces ET les dossiers.
 *
 * ⛔ Les dossiers ne sont plus déduits des chemins des pièces. Un dossier vide
 * n'existait alors pas : le client le créait, rechargeait, et il avait disparu.
 */
export const ContenuDocumentaireSchema = z.object({
  documents: z.array(DocumentSchema),
  dossiers: z.array(z.string()),
});

/** Un type de dossier ou une nature de pièce, tels que le cabinet les configure. */
export const EntreeConfigSchema = z.object({
  id: z.string(),
  cle: z.string().nullable(),
  libelle: z.string(),
  ordre: z.number(),
  actif: z.boolean(),
});
export const ListeConfigSchema = z.array(EntreeConfigSchema);
export type EntreeConfig = z.infer<typeof EntreeConfigSchema>;

export const ApercuSchema = z.object({
  url: z.string(),
  affichable: z.boolean(),
  contentType: z.string(),
  nomFichier: z.string(),
  expireDansSecondes: z.number(),
});

/**
 * Notation. `noteGlobale` est NULLABLE et `cote` peut valoir « NON EVALUE » :
 * l'écran doit savoir afficher l'absence de note. Le typage l'y oblige.
 */
export const NotationSchema = z.object({
  evaluable: z.boolean(),
  noteGlobale: z.number().nullable(),
  cote: z.string(),
  motif: z.string().nullable(),
  domaines: z.array(
    z.object({
      domaine: z.string(),
      libelle: z.string(),
      ponderation: z.number(),
      note: z.number(),
      nbConstats: z.number(),
    }),
  ),
});
export type Notation = z.infer<typeof NotationSchema>;

/**
 * Les pièces seules, pour les écrans qui n'ont que faire des dossiers vides.
 *
 * La route rend désormais `{ documents, dossiers }`. Plutôt que de reprendre
 * chaque appelant, la transformation vit ICI : le contrat serveur a une seule
 * definition, et les écrans qui ne veulent que la liste continuent de la
 * recevoir telle quelle.
 */
export const ListeDocumentsSchema = ContenuDocumentaireSchema.transform((c) => c.documents);
